if (!customElements.get('image-strip-slider')) {
  customElements.define('image-strip-slider', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.image-slider__track');
      if (!this.track) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.playButton = this.querySelector('.image-slider__autoplay');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.interval = Math.max(2000, Number(this.dataset.interval) || 5000);
      this.paused = false;
      this.hovered = false;
      this.interacting = false;
      this.editorSelection = false;
      this.visible = false;

      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') {
          this.hovered = true;
          this.schedule();
        }
      }, options);
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.schedule();
      }, options);
      this.track.addEventListener('pointerdown', () => {
        this.interacting = true;
        this.schedule();
      }, options);
      const release = () => {
        this.interacting = false;
        this.schedule();
      };
      document.addEventListener('pointerup', release, options);
      document.addEventListener('pointercancel', release, options);
      this.addEventListener('focusin', () => this.schedule(), options);
      this.addEventListener('focusout', () => queueMicrotask(() => this.schedule()), options);
      this.track.addEventListener('wheel', () => this.schedule(), { ...options, passive: true });
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => this.refresh(), options);
      this.playButton?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.refresh();
      }, options);

      this.addEventListener('shopify:block:select', event => {
        const slide = event.target.closest('.image-slider__slide');
        if (!slide) return;
        this.editorSelection = true;
        this.schedule();
        slide.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'auto' });
      }, options);
      this.addEventListener('shopify:block:deselect', () => {
        this.editorSelection = false;
        this.schedule();
      }, options);

      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(this.track);
      this.visibilityObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.schedule();
      });
      this.visibilityObserver.observe(this);
      this.refresh();
    }

    refresh() {
      this.canAutoplay = this.dataset.autoplay === 'true' &&
        this.track.scrollWidth > this.track.clientWidth + 1 && !this.motion.matches;
      if (this.playButton) {
        this.playButton.hidden = !this.canAutoplay;
        this.playButton.textContent = this.paused ? 'Play' : 'Pause';
        this.playButton.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
      }
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.isConnected || !this.canAutoplay || this.paused || this.hovered ||
          this.interacting || this.editorSelection || !this.visible || document.hidden ||
          this.track.contains(document.activeElement)) return;

      this.timer = setTimeout(() => {
        const firstSlide = this.track.firstElementChild;
        if (!firstSlide) return;
        const styles = getComputedStyle(this.track);
        const step = firstSlide.getBoundingClientRect().width + (parseFloat(styles.columnGap) || 0);
        const max = this.track.scrollWidth - this.track.clientWidth;
        const current = Math.abs(this.track.scrollLeft);
        const next = current >= max - 1 ? 0 : Math.min((Math.floor((current + 1) / step) + 1) * step, max);
        this.track.scrollTo({ left: styles.direction === 'rtl' ? -next : next, behavior: 'smooth' });
        this.schedule();
      }, this.interval);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
    }
  });
}

if (!customElements.get('image-slider-video')) {
  customElements.define('image-slider-video', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('.image-slider__video-toggle');
      if (!this.video || !this.button) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.video.controls = false;
      this.button.hidden = false;

      this.button.addEventListener('click', () => {
        // Explicit playback choices take precedence over autoplay and reduced motion.
        this.userPlayback = this.video.paused;
        this.syncPlayback();
      }, options);
      for (const event of ['play', 'pause', 'ended']) {
        this.video.addEventListener(event, () => this.updateButton(), options);
      }
      this.motion.addEventListener('change', () => this.syncPlayback(), options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);

      this.visibilityObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.25;
        this.syncPlayback();
      }, { threshold: [0, 0.25] });
      this.visibilityObserver.observe(this);
      this.updateButton();
    }

    syncPlayback() {
      const wantsPlayback = this.userPlayback ?? (this.dataset.autoplay === 'true' && !this.motion.matches);
      if (this.isConnected && this.visible && !document.hidden && wantsPlayback) {
        if (this.video.paused) this.video.play().catch(() => this.updateButton());
      } else {
        this.video.pause();
      }
      this.updateButton();
    }

    updateButton() {
      const playing = !this.video.paused && !this.video.ended;
      this.button.dataset.playing = String(playing);
      this.button.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.visibilityObserver?.disconnect();
      this.video?.pause();
    }
  });
}
